import React from 'react'
import { toast } from 'react-toastify'
import { FormattedMessage } from 'react-intl'
import { Alert, Button } from 'lib/ui'
import { Failure } from 'lib/ui/components/Toast/components/Failure'
import { useCommerceHubApplePay } from 'lib/modules/commercehub'
import { Container, SaveCard } from './styled'

/**
 * Apple Pay payment-method component (architecture §2.2).
 *
 * Design decision (architecture §7 Q6): `ApplePaySession.begin()` must be called
 * synchronously from within a user-gesture event handler, or Safari throws an
 * `InvalidAccessError`. The generic checkout "Place Order" button drives payment
 * methods through `usePaymentMethod` (a Redux dispatch + `useEffect`), which is
 * NOT guaranteed to run in the same call stack as the click. This component
 * therefore renders its OWN button as the place-order trigger instead of wiring
 * into `usePaymentMethod`. The shared "Place Order" button (rendered separately
 * in the checkout aside) stays visible but is a no-op for this method - hiding/
 * disabling it for wallet methods is a UX decision out of this track's scope
 * (see FONTOS.md follow-up notes).
 */
export const CommerceHubApplePay = () => {
  const { available, canSaveCard, saveCard, setSaveCard, placeApplePayOrder } = useCommerceHubApplePay()

  const handleError = () => {
    toast(() => (
      <Failure
        messageId='app.whoops'
      />
    ), {
      autoClose: 15000
    })
  }

  if (!available) {
    return (
      <Container>
        <Alert type='info'>
          <FormattedMessage
            id='checkout.applePay.unavailable'
          />
        </Alert>
      </Container>
    )
  }

  return (
    <Container>
      {canSaveCard && (
        <SaveCard>
          <input
            id='commerce-hub-apple-pay-save-card'
            type='checkbox'
            checked={saveCard}
            onChange={(e) => setSaveCard(e.target.checked)}
          />
          <label htmlFor='commerce-hub-apple-pay-save-card'>
            <FormattedMessage
              id='checkout.wallet.label.save'
            />
          </label>
        </SaveCard>
      )}

      <Button
        type='button'
        skin='regular'
        onClick={() => placeApplePayOrder({ onError: handleError })}
      >
        <FormattedMessage
          id='checkout.applePay.action.pay'
        />
      </Button>
    </Container>
  )
}
